import { FunctionComponent, useEffect, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Form } from '@anwb/poncho/components/forms';
import { NotificationVariants } from '@anwb/poncho/components/notification';
import { checkLoggedIn } from '@anwb/poncho/utilities/api';
import { VALIDATION_FAILURE_REDIRECT_EVENTACTION } from 'constants/datalayer';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { setCustomerDataFilledFromLogin, setCustomerIsMember } from 'store/actions';
import { PLCustomerData } from 'types';
import { carContractValidator } from 'utils/stepValidators/carContractValidator';
import { overviewValidator } from 'utils/stepValidators/overviewValidator';

import { customerDataFilledFromLoginSelector, stateSelector } from '../../../../store/selectors';
import { STEPS, gotoStep } from '../../../../utils/navigation';

import { LoggedInUserInformation } from './components/LoggedInUserInformation';
import { NavigationButtons } from './components/NavigationButtons';
import { SignInPanel } from './components/SignInPanel';
import { UserDataForm } from './components/UserDataForm';
import { StyledNotification } from './components/styles/notification.styled';
import { ErrorEnum, ErrorType } from './constants';
import { checkMembershipStatus, formatFormValues } from './utils';

import { MembershipCheckSpinner, UserInformationContainer } from './styles/UserInformation.styled';

export const UserInformationV2: FunctionComponent = () => {
	const [isLoggedIn, setIsLoggedIn] = useState(checkLoggedIn());
	const [error, setError] = useState<ErrorType | undefined>(undefined);
	const [loadingMembershipCheck, setLoadingMembershipCheck] = useState<boolean>(false);
	const [loadingLoggedInUserInfo, setLoadingLoggedInUserInfo] = useState<boolean>(false);
	const navigate = useNavigate();
	const state = useSelector(stateSelector);
	const dispatch = useDispatch();
	const isCustomerDataFilledFromLogin = useSelector(customerDataFilledFromLoginSelector);

	const formHook: UseFormReturn = useForm();
	const gaFunnelInfo = useGaFunnelInfo();

	const goBackToPreviousStep = () => {
		gotoStep(STEPS.CAR_CONTRACT, VALIDATION_FAILURE_REDIRECT_EVENTACTION, navigate, gaFunnelInfo);
	};

	useScrollToTop();

	useEffect(() => {
		// when user decides to login, clear any previous form errors
		setError(undefined);
	}, [isLoggedIn]);

	useEffect(() => {
		if (!carContractValidator(state)) goBackToPreviousStep();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isCustomerDataFilledFromLogin && !isLoggedIn) {
			formHook.reset();
			dispatch(setCustomerDataFilledFromLogin(false));
		}
	}, [dispatch, formHook, isCustomerDataFilledFromLogin, isLoggedIn]);

	const handleOnSubmit = async () => {
		const { getValues, setValue } = formHook;
		setError(undefined);

		if (isLoggedIn) {
			if (overviewValidator(state)) {
				gotoStep(STEPS.OVERVIEW, 'Naar het overzicht', navigate, gaFunnelInfo, 'navigation-button');
			} else {
				setError('MISSING_FIELDS_ERROR');
			}
			return;
		}

		const customerData = getValues() as PLCustomerData;
		formatFormValues(setValue, customerData);

		setLoadingMembershipCheck(true);
		const memberStatus = await checkMembershipStatus(customerData);
		setLoadingMembershipCheck(false);
		dispatch(setCustomerIsMember(memberStatus.isMember));

		if (overviewValidator(state)) {
			gotoStep(STEPS.OVERVIEW, 'Naar het overzicht', navigate, gaFunnelInfo, 'navigation-button');
		} else {
			setError('MISSING_FIELDS_ERROR');
		}
	};

	return (
		<UserInformationContainer $loading={loadingMembershipCheck || loadingLoggedInUserInfo}>
			{(loadingMembershipCheck || loadingLoggedInUserInfo) && (
				// Custom loading style, because the loading style of new forms is broken atm
				<MembershipCheckSpinner message="Een ogenblik geduld, alsjeblieft" />
			)}
			<Form>
				{error && (
					<StyledNotification
						variant={ErrorEnum[error].variant as NotificationVariants}
						title={ErrorEnum[error].title}
					>
						{ErrorEnum[error].message}
					</StyledNotification>
				)}

				{isLoggedIn ? (
					<LoggedInUserInformation
						setError={setError}
						formHook={formHook}
						setLoadingLoggedInUserInfo={setLoadingLoggedInUserInfo}
					/>
				) : (
					<>
						<SignInPanel isLoggedIn={isLoggedIn} setIsLoggedIn={setIsLoggedIn} />
						<UserDataForm setError={setError} formHook={formHook} />
					</>
				)}

				<NavigationButtons onSubmit={formHook.handleSubmit(handleOnSubmit)} />
			</Form>
		</UserInformationContainer>
	);
};
